import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';

const CaseStudiesPanel = ({ onStatusChange }) => {
    const { request, API_BASE_URL } = useApi();
    const [cases, setCases] = useState([]);
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        title: '',
        description: '',
        result: '',
        client_name: '',
        category: '',
        revenue_increase: '',
    });
    const [errors, setErrors] = useState({});
    const [uploadingImageId, setUploadingImageId] = useState(null);
    const [imageSliderIdx, setImageSliderIdx] = useState({});

    const loadCases = async () => {
        try {
            setBusy(true);
            const data = await request('/api/v1/user-case-studies/');
            setCases(data || []);
        } catch (error) {
            onStatusChange?.('error', 'Не удалось загрузить кейсы');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        loadCases();
    }, []);

    const validate = () => {
        const newErrors = {};
        if (!form.title?.trim()) {
            newErrors.title = 'Название обязательно';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setBusy(true);
            const payload = {
                title: form.title,
                description: form.description || null,
                result: form.result || null,
                client_name: form.client_name || null,
                category: form.category || null,
                revenue_increase: form.revenue_increase ? parseInt(form.revenue_increase) : null,
            };

            if (editingId) {
                await request(`/api/v1/user-case-studies/${editingId}`, 'PUT', payload);
                onStatusChange?.('success', 'Кейс обновлён');
            } else {
                await request('/api/v1/user-case-studies/', 'POST', payload);
                onStatusChange?.('success', 'Кейс добавлен');
            }

            resetForm();
            loadCases();
        } catch (error) {
            onStatusChange?.('error', error.message || 'Ошибка при сохранении');
        } finally {
            setBusy(false);
        }
    };

    const handleEdit = (caseStudy) => {
        setEditingId(caseStudy.id);
        setForm({
            title: caseStudy.title,
            description: caseStudy.description || '',
            result: caseStudy.result || '',
            client_name: caseStudy.client_name || '',
            category: caseStudy.category || '',
            revenue_increase: caseStudy.revenue_increase || '',
        });
        setErrors({});
    };

    const handleDelete = async (caseId) => {
        if (!confirm('Удалить кейс?')) return;

        try {
            setBusy(true);
            await request(`/api/v1/user-case-studies/${caseId}`, 'DELETE');
            onStatusChange?.('success', 'Кейс удалён');
            loadCases();
        } catch (error) {
            onStatusChange?.('error', 'Не удалось удалить кейс');
        } finally {
            setBusy(false);
        }
    };

    const resetForm = () => {
        setForm({
            title: '',
            description: '',
            result: '',
            client_name: '',
            category: '',
            revenue_increase: '',
        });
        setEditingId(null);
        setErrors({});
    };

    const handleImageUpload = async (caseId, file) => {
        if (!file) return;

        try {
            setUploadingImageId(caseId);
            const formData = new FormData();
            formData.append('file', file);

            await request(`/api/v1/user-case-studies/${caseId}/image`, 'POST', formData);
            onStatusChange?.('success', 'Изображение загружено');
            loadCases();
        } catch (error) {
            onStatusChange?.('error', error.message || 'Ошибка при загрузке изображения');
        } finally {
            setUploadingImageId(null);
        }
    };

    const handleDeleteImage = async (caseId, imageIndex) => {
        if (!confirm('Удалить изображение?')) return;

        try {
            await request(`/api/v1/user-case-studies/${caseId}/image/${imageIndex}`, 'DELETE');
            onStatusChange?.('success', 'Изображение удалено');
            setImageSliderIdx((prev) => ({
                ...prev,
                [caseId]: Math.max(0, (prev[caseId] || 0) - 1),
            }));
            loadCases();
        } catch (error) {
            onStatusChange?.('error', 'Не удалось удалить изображение');
        }
    };

    return (
        <div className="case-studies-panel">
            <div className="panel">
                <div className="panel__head">
                    <div>
                        <h4>Кейсы и примеры работ</h4>
                        <p className="muted">Добавьте примеры успешных проектов и результатов.</p>
                    </div>
                </div>

                <form className="panel__form" onSubmit={handleSubmit}>
                    <div className="case-studies__form-grid">
                        <label>
                            Название проекта *
                            <input
                                type="text"
                                value={form.title}
                                onChange={handleChange('title')}
                                placeholder="Введите название проекта"
                                disabled={busy}
                            />
                            {errors.title && <span className="field-error">{errors.title}</span>}
                        </label>
                        <label>
                            Клиент
                            <input
                                type="text"
                                value={form.client_name}
                                onChange={handleChange('client_name')}
                                placeholder="Имя клиента"
                                disabled={busy}
                            />
                        </label>
                        <label>
                            Категория
                            <select
                                value={form.category}
                                onChange={handleChange('category')}
                                disabled={busy}
                            >
                                <option value="">Выберите категорию</option>
                                <option value="SEO">SEO</option>
                                <option value="Реклама">Реклама</option>
                                <option value="Маркетинг">Маркетинг</option>
                                <option value="Дизайн">Дизайн</option>
                                <option value="Продажи">Продажи</option>
                                <option value="Другое">Другое</option>
                            </select>
                        </label>
                        <label>
                            Рост выручки (%)
                            <input
                                type="number"
                                value={form.revenue_increase}
                                onChange={handleChange('revenue_increase')}
                                placeholder="100"
                                min="0"
                                disabled={busy}
                            />
                        </label>
                    </div>

                    <label>
                        Описание
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={handleChange('description')}
                            placeholder="Опишите задачу и подход"
                            disabled={busy}
                        />
                    </label>

                    <label>
                        Результаты
                        <textarea
                            rows={3}
                            value={form.result}
                            onChange={handleChange('result')}
                            placeholder="Какие результаты были достигнуты"
                            disabled={busy}
                        />
                    </label>

                    <div className="case-studies__actions">
                        <button type="submit" disabled={busy}>
                            {editingId ? 'Обновить' : 'Добавить кейс'}
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                className="ghost"
                                onClick={resetForm}
                                disabled={busy}
                            >
                                Отмена
                            </button>
                        )}
                    </div>
                </form>

                {cases.length > 0 && (
                    <div className="case-studies__list">
                        <h5>Ваши кейсы ({cases.length})</h5>
                        {cases.map((caseStudy) => (
                            <div key={caseStudy.id} className="case-studies__item">
                                <div className="case-studies__item-head">
                                    <div>
                                        <strong>{caseStudy.title}</strong>
                                        {caseStudy.client_name && (
                                            <span className="muted"> — {caseStudy.client_name}</span>
                                        )}
                                        {caseStudy.category && (
                                            <span className="badge">{caseStudy.category}</span>
                                        )}
                                        {caseStudy.revenue_increase && (
                                            <span className="badge badge--green">
                                                +{caseStudy.revenue_increase}%
                                            </span>
                                        )}
                                    </div>
                                    <div className="case-studies__item-actions">
                                        <button
                                            type="button"
                                            className="ghost"
                                            onClick={() => handleEdit(caseStudy)}
                                            disabled={busy}
                                        >
                                            Изменить
                                        </button>
                                        <button
                                            type="button"
                                            className="ghost ghost--danger"
                                            onClick={() => handleDelete(caseStudy.id)}
                                            disabled={busy}
                                        >
                                            Удалить
                                        </button>
                                    </div>
                                </div>
                                <div className="case-studies__images-section">
                                    {(caseStudy.images || []).length > 0 && (
                                        <div className="case-studies__image-slider">
                                            <div className="case-studies__image">
                                                <img
                                                    src={`${API_BASE_URL}${caseStudy.images[imageSliderIdx[caseStudy.id] || 0]}`}
                                                    alt={caseStudy.title}
                                                />
                                            </div>
                                            {caseStudy.images.length > 1 && (
                                                <div className="case-studies__image-nav">
                                                    <button
                                                        type="button"
                                                        className="ghost ghost--sm"
                                                        onClick={() =>
                                                            setImageSliderIdx((prev) => ({
                                                                ...prev,
                                                                [caseStudy.id]: Math.max(0, (prev[caseStudy.id] || 0) - 1),
                                                            }))
                                                        }
                                                        disabled={(imageSliderIdx[caseStudy.id] || 0) === 0}
                                                    >
                                                        ←
                                                    </button>
                                                    <span className="case-studies__image-counter">
                                                        {(imageSliderIdx[caseStudy.id] || 0) + 1} / {caseStudy.images.length}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="ghost ghost--sm"
                                                        onClick={() =>
                                                            setImageSliderIdx((prev) => ({
                                                                ...prev,
                                                                [caseStudy.id]: Math.min(
                                                                    caseStudy.images.length - 1,
                                                                    (prev[caseStudy.id] || 0) + 1
                                                                ),
                                                            }))
                                                        }
                                                        disabled={
                                                            (imageSliderIdx[caseStudy.id] || 0) ===
                                                            caseStudy.images.length - 1
                                                        }
                                                    >
                                                        →
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="ghost ghost--danger ghost--sm"
                                                        onClick={() =>
                                                            handleDeleteImage(caseStudy.id, imageSliderIdx[caseStudy.id] || 0)
                                                        }
                                                        title="Удалить это изображение"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {(caseStudy.images || []).length < 5 && (
                                        <label className="case-studies__image-upload">
                                            {uploadingImageId === caseStudy.id
                                                ? 'Загрузка...'
                                                : `Добавить изображение (${(caseStudy.images || []).length}/5)`}
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                onChange={(e) => handleImageUpload(caseStudy.id, e.target.files?.[0])}
                                                disabled={uploadingImageId === caseStudy.id}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    )}
                                </div>
                                {caseStudy.description && (
                                    <p className="muted">{caseStudy.description}</p>
                                )}
                                {caseStudy.result && (
                                    <div className="case-studies__result">
                                        <strong>Результат:</strong> {caseStudy.result}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {cases.length === 0 && !busy && (
                    <div className="muted" style={{ marginTop: '20px' }}>
                        Кейсы ещё не добавлены.
                    </div>
                )}
            </div>
        </div>
    );
};

export default CaseStudiesPanel;
